import React, { useState, useEffect } from 'react'
import { Col, Container, Row, Table, Card, Badge, Form, Button, InputGroup, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Header from '../../layouts/Header';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { Search, Filter, Download, Eye, Edit, Calendar, Check, X, Clock, CheckCircle } from 'lucide-react';
import { Pencil } from 'react-bootstrap-icons';

const StockRequest = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const { access } = useSelector((state) => state.login);
    const accessToken = localStorage.getItem('authToken');
    const [stockRequests, setStockRequests] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [updatingDelivery, setUpdatingDelivery] = useState(false);

    // Filter requests based on search and status
    const filteredRequests = stockRequests.filter(request => {
        const matchesSearch = request.indentNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            request.outlet?.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'ALL' || request.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });
            if (response?.data?.status === true) {
                setOutlets(response?.data?.data?.outlets);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const getStockRequests = async (outlet = '') => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            let url = `material-request-indent`;
            if (outlet) url += `?outlet=${outlet}`;
            
            const response = await axios.get(url, { headers });
            if (response?.data?.status === true) {
                setStockRequests(response?.data?.data?.data || []);
            }
        } catch (error) {
            handleApiError(error);
            setStockRequests([]);
        } finally {
            setLoading(false);
        }
    };

    // Update status API
    const updateRequestStatus = async (requestData) => {
        setUpdating(true);
        try {
            const headers = { 
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            };
            
            const payload = {
                _id: requestData._id,
                status: requestData.status,
                items: requestData.items.map(item => ({
                    itemId: item._id,
                    status: item.status
                }))
            };

            console.log("Updating status with payload:", payload);

            const response = await axios.put('material-request-indent', payload, { headers });
            
            if (response?.data?.status === true) {
                toast.success('Request status updated successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
                
                // Refresh the data
                getStockRequests(selectedOutlet);
                setSelectedRequest(null);
                return true;
            }
        } catch (error) {
            handleApiError(error);
            return false;
        } finally {
            setUpdating(false);
        }
    };

    // Update delivery date API
    const updateDeliveryDate = async (item) => {
        setUpdatingDelivery(true);
        try {
            const headers = { 
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            };
            
            const payload = {
                _id: selectedRequest._id,
                rawMaterialId: item.rawMaterial._id,
                approvedBy: item.approvedBy || '',
                expectedDeliveryDate: item.expectedDeliveryDate || null
            };

            console.log("Updating delivery date with payload:", payload);

            const response = await axios.put('material-request-indent/update-expected-delivery-date', payload, { headers });
            
            if (response?.data?.status === true) {
                toast.success('Delivery date updated successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
                
                // Refresh the data
                getStockRequests(selectedOutlet);
                return true;
            }
        } catch (error) {
            handleApiError(error);
            return false;
        } finally {
            setUpdatingDelivery(false);
        }
    };

    const handleApiError = (error) => {
        if (error.response && error.response?.data?.status === false) {
            const errorMessage = error.response.data.error;
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'PENDING': { variant: 'warning', text: 'Pending', icon: '⏳' },
            'APPROVED': { variant: 'primary', text: 'Approved', icon: '✅' },
            'COMPLETED': { variant: 'success', text: 'Completed', icon: '✔️' },
            'REJECTED': { variant: 'danger', text: 'Rejected', icon: '❌' }
        };
        const config = statusConfig[status] || { variant: 'secondary', text: status, icon: '📄' };
        return <Badge bg={config.variant} className="d-flex align-items-center justify-content-center gap-1 w-75 w-semibold fs-8" style={{ width: 'fit-content' }}>
            {config.icon} {config.text}
        </Badge>;
    };

    const getItemStatusBadge = (status) => {
        const statusConfig = {
            'PENDING': { variant: 'warning', text: 'Pending' },
            'APPROVED': { variant: 'primary', text: 'Approved' },
            'COMPLETED': { variant: 'success', text: 'Completed' },
            'REJECTED': { variant: 'danger', text: 'Rejected' }
        };
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant} className="fs-8 fw-semibold w-75">{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusCount = (status) => {
        return stockRequests.filter(req => req.status === status).length;
    };

    // Quick status update handlers
    const handleQuickApprove = async (request) => {
        const updatedRequest = {
            ...request,
            status: 'APPROVED',
            items: request.items.map(item => ({
                ...item,
                status: item.status === 'REJECTED' ? 'REJECTED' : 'APPROVED'
            }))
        };
        
        await updateRequestStatus(updatedRequest);
    };

    const handleQuickComplete = async (request) => {
        const updatedRequest = {
            ...request,
            status: 'COMPLETED',
            items: request.items.map(item => ({
                ...item,
                status: item.status === 'REJECTED' ? 'REJECTED' : 'COMPLETED'
            }))
        };
        
        await updateRequestStatus(updatedRequest);
    };

    const handleQuickReject = async (request) => {
        const updatedRequest = {
            ...request,
            status: 'REJECTED',
            items: request.items.map(item => ({
                ...item,
                status: 'REJECTED'
            }))
        };
        
        await updateRequestStatus(updatedRequest);
    };

    // Get available actions based on current status
    const getAvailableActions = (request) => {
        const actions = [];
        
        switch (request.status) {
            case 'PENDING':
                actions.push(
                    {
                        variant: 'outline-success',
                        label: 'Approve',
                        icon: <Check size={14} />,
                        onClick: () => handleQuickApprove(request)
                    },
                    {
                        variant: 'outline-danger',
                        label: 'Reject',
                        icon: <X size={14} />,
                        onClick: () => handleQuickReject(request)
                    }
                );
                break;
            case 'APPROVED':
                actions.push(
                    // {
                    //     variant: 'outline-success',
                    //     label: 'Complete',
                    //     icon: <CheckCircle size={14} />,
                    //     onClick: () => handleQuickComplete(request)
                    // },
                    // {
                    //     variant: 'outline-danger',
                    //     label: 'Reject',
                    //     icon: <X size={14} />,
                    //     onClick: () => handleQuickReject(request)
                    // }
                );
                break;
            case 'COMPLETED':
                // No quick actions for completed requests
                break;
            case 'REJECTED':
                actions.push(
                    {
                        variant: 'outline-success',
                        label: 'Approve',
                        icon: <Check size={14} />,
                        onClick: () => handleQuickApprove(request)
                    }
                );
                break;
        }
        
        return actions;
    };

    useEffect(() => {
        getStockRequests();
        getOutlets();
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-3 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
                <Container fluid>
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">STOCK REQUESTS</h4>
                            <p className="text-muted mb-0">Manage and track material requests from outlets</p>
                        </div>
                        <Button variant="primary" onClick={() => getStockRequests(selectedOutlet)} disabled={loading}>
                            {loading ? 'Refreshing...' : 'Refresh Data'}
                        </Button>
                    </div>

                    {/* Quick Stats */}
                    <Row className="mb-4">
                        <Col md={3}>
                            <Card className={`h-100 border-0 ${skin === 'dark' ? 'bg-dark' : 'bg-white'}`}>
                                <Card.Body className="text-center">
                                    <div className="text-warning mb-2">⏳</div>
                                    <h3 className="fw-bold">{getStatusCount('PENDING')}</h3>
                                    <p className="mb-0 text-muted">Pending</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className={`h-100 border-0 ${skin === 'dark' ? 'bg-dark' : 'bg-white'}`}>
                                <Card.Body className="text-center">
                                    <div className="text-primary mb-2">✅</div>
                                    <h3 className="fw-bold">{getStatusCount('APPROVED')}</h3>
                                    <p className="mb-0 text-muted">Approved</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className={`h-100 border-0 ${skin === 'dark' ? 'bg-dark' : 'bg-white'}`}>
                                <Card.Body className="text-center">
                                    <div className="text-success mb-2">✔️</div>
                                    <h3 className="fw-bold">{getStatusCount('COMPLETED')}</h3>
                                    <p className="mb-0 text-muted">Completed</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className={`h-100 border-0 ${skin === 'dark' ? 'bg-dark' : 'bg-white'}`}>
                                <Card.Body className="text-center">
                                    <div className="text-danger mb-2">❌</div>
                                    <h3 className="fw-bold">{getStatusCount('REJECTED')}</h3>
                                    <p className="mb-0 text-muted">Rejected</p>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filters */}
                    <Card className={`border-0 mb-4 ${skin === 'dark' ? 'bg-dark' : 'bg-white'}`}>
                        <Card.Body>
                            <Row className="g-3 align-items-center">
                                <Col md={3}>
                                    <Form.Select 
                                        value={selectedOutlet}
                                        onChange={(e) => {
                                            setSelectedOutlet(e.target.value);
                                            getStockRequests(e.target.value);
                                        }}
                                        className={skin === 'dark' ? 'bg-dark border-secondary text-light' : ''}
                                    >
                                        <option value="" >All Outlets</option>
                                        {outlets.map((outlet) => (
                                            <option key={outlet._id} value={outlet._id}>
                                                {outlet.name || `${outlet.city} ${outlet?.state}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                 <Col md={3}>
                                    <Form.Select 
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className={skin === 'dark' ? 'bg-dark border-secondary text-light' : ''}
                                    >
                                        <option value="ALL">All Status</option>
                                        {/* <option value="PENDING">Pending</option> */}
                                        <option value="APPROVED">Approved</option>
                                        {/* <option value="COMPLETED">Completed</option> */}
                                        <option value="REJECTED">Rejected</option>
                                    </Form.Select>
                                </Col>
                                <Col md={4}>
                                    <InputGroup>
                                        <InputGroup.Text className={skin === 'dark' ? 'bg-dark border-secondary' : ''}>
                                            <Search size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Search by indent number or outlet..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className={skin === 'dark' ? 'bg-dark border-secondary text-light' : ''}
                                        />
                                    </InputGroup>
                                </Col>
                                {/* <Col md={2}>
                                    <Button variant="outline-secondary" className="w-100 d-flex align-items-center justify-content-center gap-2">
                                        <Filter size={16} />
                                        More Filters
                                    </Button>
                                </Col> */}
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Main Table */}
                    <Card className={`border-0 ${skin === 'dark' ? 'bg-dark' : 'bg-white'} p-2`}>
                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading stock requests...</p>
                                </div>
                            ) : filteredRequests.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="text-muted mb-3">📭</div>
                                    <h5 className="text-muted">No stock requests found</h5>
                                    <p className="text-muted">
                                        {searchTerm || statusFilter !== 'ALL' || selectedOutlet 
                                            ? 'Try changing your filters or search term' 
                                            : 'No stock requests have been created yet'}
                                    </p>
                                </div>
                            ) : (
                                <div className="table-responsive table-dark">
                                    <Table hover className="mb-0">
                                        <thead className={skin === 'dark' ? 'table-dark' : 'table-white'}>
                                            <tr>
                                                <th>SR NO</th>
                                                <th>REQUEST DETAILS</th>
                                                <th>OUTLET</th>
                                                <th>ITEMS</th>
                                                <th>DATE</th>
                                                <th>STATUS</th>
                                                <th>ACTIONS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredRequests.map((request, index) => {
                                                const availableActions = getAvailableActions(request);
                                                return (
                                                    <tr key={request._id} className={skin === 'dark' ? 'table-dark' : ''}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <div>
                                                                <strong className="d-block">{request.indentNumber}</strong>
                                                                <small className="text-muted">Financial Year: {request.fin_year}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <strong className="d-block">
                                                                    {request?.outlet?.name || 'Unknown Outlet'}
                                                                </strong>
                                                                <small className="text-muted">
                                                                    {request?.outlet?.city}, {request?.outlet?.state}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Badge bg="outline-primary" className="bg-light text-dark border">
                                                                {request.items?.length || 0} items
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <small className="d-block">{formatDate(request.createdAt)}</small>
                                                                <small className="text-muted">Created</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {getStatusBadge(request.status)}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    onClick={() => setSelectedRequest(request)}
                                                                    className="d-flex align-items-center gap-1"
                                                                >
                                                                    <Eye size={14} />
                                                                    View
                                                                </Button>
                                                                {availableActions?.map((action, index) => (
                                                                    <Button 
                                                                        key={index}
                                                                        variant={action.variant} 
                                                                        size="sm"
                                                                        onClick={action.onClick}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        {action.icon}
                                                                        {action.label}
                                                                    </Button>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Update Status Modal */}
                    <Modal show={selectedRequest !== null} onHide={() => setSelectedRequest(null)} size="lg">
                        <Modal.Header closeButton className={skin === 'dark' ? 'bg-dark text-light' : ''}>
                            <Modal.Title>
                                Update Request Status - {selectedRequest?.indentNumber}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className={skin === 'dark' ? 'bg-dark text-light' : ''}>
                            {selectedRequest && (
                                <>
                                    {/* Request Overview */}
                                    <Card className={`mb-4 ${skin === 'dark' ? 'bg-dark border-secondary' : ''}`}>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <strong>Outlet:</strong> {selectedRequest.outlet?.outletName}
                                                </Col>
                                                <Col md={6}>
                                                    <strong>Created:</strong> {formatDate(selectedRequest.createdAt)}
                                                </Col>
                                            </Row>
                                            <Row className="mt-2">
                                                <Col md={6}>
                                                    <strong>Financial Year:</strong> {selectedRequest.fin_year}
                                                </Col>
                                                <Col md={6}>
                                                    <div className='d-flex'>
                                                        <strong>Current Status:</strong> 
                                                        <div className='ms-2 w-50'>
                                                            {getStatusBadge(selectedRequest.status)}
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    {/* Overall Request Status Update */}
                                    <Card className={`mb-4 ${skin === 'dark' ? 'bg-dark border-secondary' : ''}`}>
                                        <Card.Header>
                                            <h6 className="mb-0">Update Overall Request Status</h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col>
                                                    <Form.Group>
                                                        <Form.Label>Request Status</Form.Label>
                                                        <Form.Select 
                                                            value={selectedRequest.status}
                                                            onChange={(e) => {
                                                                setSelectedRequest({
                                                                    ...selectedRequest,
                                                                    status: e.target.value
                                                                });
                                                            }}
                                                            className={skin === 'dark' ? 'bg-dark border-secondary text-light' : ''}
                                                        >
                                                            {/* <option value="PENDING">Pending</option> */}
                                                            <option value="APPROVED">Approved</option>
                                                            {/* <option value="COMPLETED">Completed</option> */}
                                                            <option value="REJECTED">Rejected</option>
                                                        </Form.Select>
                                                        <Form.Text className="text-muted">
                                                            This will update the overall request status
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                <div className='d-flex flex-column'>
                                                    <Form.Label className='text-white'>.</Form.Label>
                                                    <Button
                                                        size='sm' 
                                                        variant="success" 
                                                        onClick={() => updateRequestStatus(selectedRequest)}
                                                        disabled={updating}
                                                    >
                                                        <Pencil className='me-2' />
                                                        {updating ? 'Updating...' : 'Update Status'}
                                                    </Button>
                                                </div>
                                                </Col>
                                            </Row>
                                            
                                            
                                        </Card.Body>
                                    </Card>

                                    {/* Individual Items Status */}
                                    <Card className={`${skin === 'dark' ? 'bg-dark border-secondary' : ''}`}>
                                        <Card.Header>
                                            <h6 className="mb-0">Update Individual Items</h6>
                                        </Card.Header>
                                        <Card.Body>
                                            {selectedRequest.items?.map((item, index) => (
                                                <div key={item._id} className="border-bottom pb-3 mb-3">
                                                    <Row className="align-items-center">
                                                        <Col md={5}>
                                                            <strong>{item.rawMaterial?.name}</strong>
                                                            <div className='w-50 mb-0'>
                                                                {getItemStatusBadge(item.status)}
                                                            </div>
                                                            <br />
                                                            <small className="text-muted mt-0">
                                                                Requested: {item.requestedQuantity} {item.rawMaterial?.unit}
                                                            </small>
                                                            <br />
                                                            <small className="text-muted">
                                                                Received: {item.recievedQuantity} {item.rawMaterial?.unit}
                                                            </small>
                                                            <br />
                                                            <small className="text-muted">
                                                                Approved By: {item.approvedBy || 'Not set'}
                                                            </small>
                                                            <br />
                                                            <small className="text-muted">
                                                                Approved Date: {item.apprrovedDate ? formatDate(item.apprrovedDate) : 'Not set'}
                                                            </small>
                                                            <br />
                                                            <small className="text-muted">
                                                                Expected Delivery: {item.expectedDeliveryDate ? formatDate(item.expectedDeliveryDate) : 'Not set'}
                                                            </small>
                                                            <br />
                                                            <small className='text-muted'>
                                                                Remark: {item?.remark ? item?.remark : 'Remark has not been added'}
                                                            </small>
                                                        </Col>
                                                        <Col md={7}>
                                                            <Row>
                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label>Item Status</Form.Label>
                                                                        <Form.Select 
                                                                            value={item.status}
                                                                            onChange={(e) => {
                                                                                const updatedItems = [...selectedRequest.items];
                                                                                updatedItems[index] = {
                                                                                    ...item,
                                                                                    status: e.target.value
                                                                                };
                                                                                setSelectedRequest({
                                                                                    ...selectedRequest,
                                                                                    items: updatedItems
                                                                                });
                                                                            }}
                                                                            className={skin === 'dark' ? 'bg-dark border-secondary text-light' : ''}
                                                                        >
                                                                            {/* <option value="PENDING">Pending</option> */}
                                                                            <option value="APPROVED">Approved</option>
                                                                            {/* <option value="COMPLETED">Completed</option> */}
                                                                            <option value="REJECTED">Rejected</option>
                                                                        </Form.Select>
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className='d-flex flex-column'>
                                                                        <Form.Label className='text-white'>.</Form.Label>
                                                                        <Button
                                                                            size='sm' 
                                                                            variant="success" 
                                                                            onClick={() => updateRequestStatus(selectedRequest)}
                                                                            disabled={updating}
                                                                        >
                                                                            <Pencil className='me-2' />
                                                                            {updating ? 'Updating...' : 'Update Status'}
                                                                        </Button>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                            {/* <Row>
                                                                <Col></Col>
                                                                <Col className='text-end'>
                                                                    <Button
                                                                        size='sm' 
                                                                        variant="primary" 
                                                                        onClick={() => updateRequestStatus(selectedRequest)}
                                                                        disabled={updating}
                                                                    >
                                                                        <Pencil className='me-2' />
                                                                        {updating ? 'Updating...' : 'Update Status'}
                                                                    </Button>
                                                                </Col>
                                                            </Row> */}

                                                            {/* <hr /> */}
                                                            
                                                            {/* <Row>
                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label>Approved By</Form.Label>
                                                                        <Form.Control
                                                                            type="text"
                                                                            placeholder="Enter approver name"
                                                                            value={item.approvedBy || ''}
                                                                            onChange={(e) => {
                                                                                const updatedItems = [...selectedRequest.items];
                                                                                updatedItems[index] = {
                                                                                    ...item,
                                                                                    approvedBy: e.target.value
                                                                                };
                                                                                setSelectedRequest({
                                                                                    ...selectedRequest,
                                                                                    items: updatedItems
                                                                                });
                                                                            }}
                                                                            className={skin === 'dark' ? 'bg-dark border-secondary text-light' : ''}
                                                                        />
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label>Expected Delivery Date</Form.Label>
                                                                        <Form.Control
                                                                            type="date"
                                                                            value={item.expectedDeliveryDate ? new Date(item.expectedDeliveryDate).toISOString().split('T')[0] : ''}
                                                                            onChange={(e) => {
                                                                                const updatedItems = [...selectedRequest.items];
                                                                                updatedItems[index] = {
                                                                                    ...item,
                                                                                    expectedDeliveryDate: e.target.value ? new Date(e.target.value).toISOString() : null
                                                                                };
                                                                                setSelectedRequest({
                                                                                    ...selectedRequest,
                                                                                    items: updatedItems
                                                                                });
                                                                            }}
                                                                            className={skin === 'dark' ? 'bg-dark border-secondary text-light' : ''}
                                                                        />
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row> */}
                                                            
                                                            {/* <div className="d-flex justify-content-end">
                                                                <Button 
                                                                    variant="primary" 
                                                                    size="sm"
                                                                    onClick={() => updateDeliveryDate(item)}
                                                                    disabled={updatingDelivery}
                                                                    className="d-flex align-items-center justify-content-center gap-1 mt-1 w-50"
                                                                >
                                                                    <Calendar size={14} />
                                                                    {updatingDelivery ? 'Updating...' : 'Update Delivery'}
                                                                </Button>
                                                            </div> */}
                                                        </Col>
                                                    </Row>
                                                </div>
                                            ))}
                                        </Card.Body>
                                    </Card>
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer className={skin === 'dark' ? 'bg-dark border-secondary' : ''}>
                            <Button variant="secondary" className='w-25' onClick={() => setSelectedRequest(null)}>
                                Cancel
                            </Button>
                            {/* <Button 
                                variant="primary" 
                                onClick={() => updateRequestStatus(selectedRequest)}
                                disabled={updating}
                            >
                                {updating ? 'Updating...' : 'Update Status'}
                            </Button> */}
                        </Modal.Footer>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default StockRequest;